import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { isEqual, isNil } from 'lodash';
import { bindActionCreators } from 'redux';

import beDeleteTimeOffCategory from 'actions/timeoff/beDeleteTimeOffCategory.action';
import beSaveTimeOffCategory from 'actions/timeoff/beSaveTimeOffCategory.action';
import beTimeOffReport from 'actions/timeoff/beTimeOffReport.action';
import feDeleteLocalTimeOffCategory from 'actions/timeoff/feDeleteLocalTimeOffCategory.action';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog.component';
import TypedOption from 'components/form/TypedOption.component';
import ColorPicker from 'components/generic/colorPicker/ColorPicker.component';
import Tooltip from 'components/generic/Tooltip.component';
import DeleteCategoryButton from 'components/hr/categories/DeleteCategoryButton.component';
import AllowanceInput from 'components/hr/shared/AllowanceInput.component';
import TIME_OFF_COLORS from 'constants/timeOffColors.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { isEnvMobile } from 'utils/env/determine.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFn from 'utils/predicates/function/isFn.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import { capitalize } from 'utils/strings/manipulate.util';
import constant from 'utils/values/constant.util';

import CN from './TimeOffCategory.module.scss';

const L = logger('TimeOffCategory');

const TEMP_CAT = 'tempCat';

const ALERTS = constant({
  success: 'success',
  danger: 'danger',
});

const getAlertMessage = (t) =>
  constant({
    save: t('humanResources.timeOffSettings.categories.alertMessages.save'),
    saveFail: t('humanResources.timeOffSettings.categories.alertMessages.saveFail'),
    delete: t('humanResources.timeOffSettings.categories.alertMessages.delete'),
    deleteFail: t('humanResources.timeOffSettings.categories.alertMessages.deleteFail'),
    error: t('humanResources.timeOffSettings.categories.alertMessages.error'),
  });

const TimeOffCategory = ({ category: categoryFromProps, onAlert, feDeleteLocalTimeOffCategory, beDeleteTimeOffCategory, beTimeOffReport, beSaveTimeOffCategory }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const isMobile = isEnvMobile();
  const salonId = useCurrentSalonId();

  const category = asObject(categoryFromProps);
  const categoryId = fromAnyToString(category.id);
  const isTemp = categoryId.includes(TEMP_CAT);
  const [localCategory, setLocalCategory] = useState(category);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const { val: isEdited, set: setIsEdited } = useTip(false);
  const { val: deleteModal, yay: openDeleteModal, nay: closeDeleteModal } = useTip(false);

  const tooltipPosition = isMobile ? 'bottomBottom' : 'top';

  const alertMessage = getAlertMessage(t);

  const handleChange = useCallback(
    (ev) => {
      if (isFn(ev.persist)) {
        ev.persist();
      }

      // value will be controlled depending on the input type,
      // i.e for name it will be capitalized
      let value = getAnyOrVoid(isNil(ev.value) ? 'target.value' : 'value', ev);
      const fieldName = getAnyOrVoid(isNil(ev.name) ? 'target.name' : 'name', ev);
      if (fieldName === 'defaultAllowance') {
        value = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
      }
      if (fieldName === 'name') {
        setIsSaveDisabled(isFalsy(value));
        value = capitalize(value);
      }

      const tempCategory = { ...localCategory, [fieldName]: value };

      const isNotEqual = !isEqual(category, tempCategory);
      setIsEdited(isNotEqual);
      setLocalCategory(tempCategory);
    },
    [category, localCategory, setIsEdited],
  );

  const handleDelete = useCallback(() => {
    closeDeleteModal();

    if (isTemp) {
      feDeleteLocalTimeOffCategory({ id: categoryId })
        .then(({ response, error }) => {
          if (!response && !error) {
            return;
          }

          const options = error
            ? { type: ALERTS.danger, message: alertMessage.deleteFail }
            : {
                type: ALERTS.success,
                message: alertMessage.delete,
              };

          call('onAlert', onAlert, options);
        })
        .catch((err) => {
          call('onAlert', onAlert, { type: ALERTS.danger, message: alertMessage.error });
          throw err;
        });
      return;
    }

    beDeleteTimeOffCategory({ salonId, id: categoryId })
      .then(({ response, error }) => {
        if (!response && !error) {
          return;
        }

        const options = error
          ? { type: ALERTS.danger, message: alertMessage.deleteFail }
          : {
              type: ALERTS.success,
              message: alertMessage.delete,
            };

        call('onAlert', onAlert, options);
      })
      .catch((err) => {
        call('onAlert', onAlert, { type: ALERTS.danger, message: alertMessage.error });
        throw err;
      });
  }, [alertMessage.delete, alertMessage.deleteFail, alertMessage.error, beDeleteTimeOffCategory, categoryId, closeDeleteModal, feDeleteLocalTimeOffCategory, isTemp, onAlert, salonId]);

  const handleUndo = useCallback(() => {
    setLocalCategory(category);
    setIsEdited(false);
  }, [category, setIsEdited]);

  const handleSave = useCallback(() => {
    const options = {
      salonId,
      isNew: isTemp,
      category: {
        id: localCategory.id,
        salon_id: localCategory.salonId,
        name: localCategory.name,
        color: localCategory.color,
        is_working_hours: localCategory.isWorkingHours,
        default_allowance: localCategory.defaultAllowance,
      },
    };

    beSaveTimeOffCategory(options)
      .then(({ response, error }) => {
        if (!response && !error) {
          return;
        }

        if (response) {
          setIsEdited(false);
          L.unignore('useEffect()', 'beTimeOffReport:', beTimeOffReport({ salonId }));
        }

        const options = error ? { type: ALERTS.danger, message: alertMessage.saveFail } : { type: ALERTS.success, message: alertMessage.save };

        call('onAlert', onAlert, options);
      })
      .catch((err) => {
        onAlert({ type: ALERTS.danger, message: alertMessage.error });
        throw err;
      });
  }, [
    salonId,
    isTemp,
    localCategory.id,
    localCategory.salonId,
    localCategory.name,
    localCategory.color,
    localCategory.isWorkingHours,
    localCategory.defaultAllowance,
    beSaveTimeOffCategory,
    alertMessage.saveFail,
    alertMessage.save,
    alertMessage.error,
    onAlert,
    setIsEdited,
    beTimeOffReport,
  ]);

  const handleDeleteModalClose = useCallback(() => void closeDeleteModal(), [closeDeleteModal]);

  return (
    <div
      data-bem="TimeOffCategory"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {fromAnyToBool(localCategory.isFallback) ? (
        <Tooltip variant={tooltipPosition} content={t('humanResources.timeOffSettings.categories.tooltipText')} width="lg">
          <input type="text" name="name" value={localCategory.name} onChange={alwaysNoop} className={CN.input} disabled={localCategory.isFallback} />
        </Tooltip>
      ) : (
        <input type="text" name="name" value={localCategory.name} onChange={handleChange} className={CN.input} />
      )}
      <div className={CN.column}>
        <ColorPicker key={localCategory.color} name="color" active={localCategory.color} items={TIME_OFF_COLORS} onChange={handleChange} />
      </div>
      <div className={CN.column}>
        {fromAnyToBool(localCategory.isFallback) ? (
          <Tooltip variant={tooltipPosition} content={t('humanResources.timeOffSettings.categories.tooltipText')} width="lg">
            <TypedOption type="simple-checkbox" checked={false} onCheck={alwaysNoop}></TypedOption>
          </Tooltip>
        ) : (
          <TypedOption type="checkbox" key={localCategory.isWorkingHours} name="isWorkingHours" checked={localCategory.isWorkingHours} onCheck={handleChange} />
        )}
      </div>
      <div className={CN.column}>
        <AllowanceInput name="defaultAllowance" value={localCategory.defaultAllowance} onChange={handleChange} />
      </div>
      {fromAnyToBool(!localCategory.isDefault && !isEdited) && <DeleteCategoryButton onClick={openDeleteModal} />}
      {isEdited && (
        <div className={CN.column}>
          <button className={CN.save} disabled={isSaveDisabled} onClick={handleSave}>
            {t('common.buttons.saveUpp')}
          </button>
          <button className={CN.undo} onClick={handleUndo}>
            {t('humanResources.timeOffSettings.categories.undoButton')}
          </button>
        </div>
      )}
      <div data-bem="TimeOffCategory__error" className={CN.error}>
        {isFalsy(localCategory.name) && <span className={CN.error}>{t('humanResources.timeOffSettings.categories.nameInputValidationMsg')}</span>}
      </div>
      {deleteModal && (
        <ConfirmationDialog
          isOpen={deleteModal}
          leftButtonText={t('humanResources.timeOffSettings.categories.deleteCategoryModal.goBackButton')}
          onLeft={handleDeleteModalClose}
          onRight={handleDelete}
          rightButtonText={t('humanResources.timeOffSettings.categories.deleteCategoryModal.confirmButton')}
          title={t('humanResources.timeOffSettings.categories.deleteCategoryModal.title')}
        >
          <p>
            {t('humanResources.timeOffSettings.categories.deleteCategoryModal.description.part1')} {localCategory.name}{' '}
            {t('humanResources.timeOffSettings.categories.deleteCategoryModal.description.part2')}
          </p>
          <br />
          <p>{t('humanResources.timeOffSettings.categories.deleteCategoryModal.noteMessage')}</p>
        </ConfirmationDialog>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      feDeleteLocalTimeOffCategory,
      beDeleteTimeOffCategory,
      beSaveTimeOffCategory,
      beTimeOffReport,
    },
    dispatch,
  );

const TimeOffCategoryHoc = connect(null, mapDispatchToProps)(TimeOffCategory);

export default TimeOffCategoryHoc;
